import { Link } from "react-router-dom";
import {
  Sparkles,
  PaintBucket,
  Wrench,
  Lightbulb,
  Hammer,
  Sofa,
  Droplets,
  DoorOpen,
  Building2,
  ArrowRight,
  CalendarCheck,
  Siren,
  ClipboardList,
  RefreshCw,
} from "lucide-react";

const INCLUDED = [
  { icon: Sparkles, label: "Commercial & Hotel Cleaning" },
  { icon: Sparkles, label: "Window Cleaning" },
  { icon: PaintBucket, label: "Painting & Touch-Up Works" },
  { icon: Droplets, label: "Power Washing" },
  { icon: DoorOpen, label: "Door Repairs & Adjustments" },
  { icon: Lightbulb, label: "Light Bulb Replacement" },
  { icon: Wrench, label: "General Maintenance & Small Repairs" },
  { icon: Hammer, label: "Carpet Cleaning (Hotels & Commercial)" },
  { icon: CalendarCheck, label: "Planned Preventative Maintenance" },
  { icon: Siren, label: "Emergency Call-outs" },
  { icon: ClipboardList, label: "Building Compliance Support" },
  { icon: RefreshCw, label: "Reactive Maintenance" },
];

const IDEAL_FOR = [
  "Hotels",
  "Office Buildings",
  "Commercial Properties",
  "Warehouses",
  "Factories",
  "Retail Units",
  "Industrial Sites",
];

const STEPS = [
  { k: "01", v: "We assess your property requirements" },
  { k: "02", v: "We create a tailored maintenance plan" },
  { k: "03", v: "Our team delivers scheduled or one-time services" },
  { k: "04", v: "Your facility stays clean, safe and fully maintained" },
];

const WHY = [
  "One company for all facility needs",
  "Reliable hotel & commercial cleaning standards",
  "Fully insured & trained team",
  "Fast response for maintenance issues",
  "Flexible contracts (regular or one-time)",
  "Cost-effective all-in-one solution",
];

export default function FacilityManagement() {
  return (
    <section
      id="facility-management"
      data-testid="facility-management-section"
      className="bg-white py-24 md:py-36 border-t border-[#E5EBF2] relative"
    >
      <div className="relative container-ags">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                Facility Management Package
              </span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.035em] text-[#050B14]">
              One team. One package.
              <br />
              <span className="italic-display font-normal text-[#0E2A57]">
                Complete facility care.
              </span>
            </h2>
            <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-2xl">
              A complete commercial Facility Management package across Ireland
              for hotels, offices, warehouses, and industrial sites. We combine
              every essential cleaning and maintenance need into one reliable
              solution — handled by one accountable family-owned team.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <Link
              to="/quote"
              data-testid="facility-cta-quote"
              className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] px-10 py-5 font-semibold tracking-wide transition-colors"
            >
              Get a Free Quote
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* Included services */}
        <div className="mb-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold mb-5">
            Included Services
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5EBF2] border border-[#E5EBF2]">
            {INCLUDED.map((it, i) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  data-testid={`facility-included-${i}`}
                  className="bg-white p-5 hover:bg-[#F4F8FC] transition-colors flex items-center gap-4"
                >
                  <span className="bg-[#0E2A57] text-[#BEE7F5] w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  <span className="text-sm md:text-base font-semibold text-[#0E2A57]">
                    {it.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ideal for + How it works */}
        <div className="grid md:grid-cols-12 gap-px bg-[#E5EBF2] border border-[#E5EBF2] mb-12">
          <div className="md:col-span-5 bg-white p-6 md:p-8">
            <div className="flex items-center gap-3">
              <Building2 size={20} className="text-[#2BB6E5]" />
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                Ideal For
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {IDEAL_FOR.map((t) => (
                <span
                  key={t}
                  className="inline-block bg-[#F4F8FC] text-[#0E2A57] border border-[#0E2A57]/15 px-3 py-1.5 text-sm font-semibold"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-7 bg-white p-6 md:p-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              How It Works
            </div>
            <ol className="mt-5 space-y-4">
              {STEPS.map((s) => (
                <li key={s.k} className="flex items-start gap-4">
                  <span className="bg-[#0E2A57] text-white w-8 h-8 flex items-center justify-center font-heading font-black text-sm flex-shrink-0">
                    {s.k}
                  </span>
                  <span className="text-[#050B14] text-sm md:text-base leading-relaxed pt-1">
                    {s.v}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Why choose our package */}
        <div className="bg-[#0E2A57] text-white p-8 md:p-10">
          <div className="grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-4">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                Why Choose Our Package
              </div>
              <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl leading-tight text-white">
                Everything your facility needs — under one accountable roof.
              </h3>
            </div>
            <ul className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {WHY.map((w) => (
                <li key={w} className="flex items-start gap-3 text-white/90 text-sm md:text-base">
                  <span className="text-[#2BB6E5] font-bold mt-0.5">→</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
